import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import s from "../modules/TrainerCreate.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/**
 * Страница: Добавить тренажёр (одношаговая форма с CKEditor 4)
 * Создание тренажёра: POST https://apitr.subxonqulov.uz/api/vazifalar/
 *   Поля: name, savol (HTML), img, promt, qisqa_tavsif, tartib, reja (HTML), fan (id),
 *         user (id), AI (id), tushuncha_b2 (HTML)
 * После успешного создания перенаправляет на /trainer/my
 */

const VAZIFALAR_ENDPOINT =
  process.env.REACT_APP_VAZIFALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/vazifalar/";

const AIKEYS_ENDPOINT =
  process.env.REACT_APP_AIKEYS_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/aikeys/";

const FANS_ENDPOINT =
  process.env.REACT_APP_FANS_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/fanlar/";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME; // "Bearer" | "Token" | "JWT"
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

export default function TrainerCreate() {

  const t = useT();
  const ready = useI18nReady();

  const navigate = useNavigate();

  // ===== Шаг 1: форма тренажёра
  const [form, setForm] = useState({
    name: "",
    qisqa_tavsif: "",
    AI: "", // id ИИ
    tartib: "", // порядок
    fan: "", // id дисциплины
  });
  const [rejaHTML, setRejaHTML] = useState(""); // CKEditor - reja (план)
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [ais, setAis] = useState([]);
  const [fans, setFans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const user = useMemo(() => getStoredUser(), []);

  // CKEditor refs & ids
  const editor1Ref = useRef(null);
  const editorId1 = useMemo(() => "tushuncha-editor-" + Math.random().toString(36).slice(2), []);

  // Load AI list
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(AIKEYS_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => []);
        if (!abort) setAis(Array.isArray(data) ? data : []);
      } catch {
        if (!abort) setAis([]);
      }
    })();
    return () => { abort = true; };
  }, []);

  // Load Fans (disciplines) list
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(FANS_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => []);
        if (!abort) setFans(Array.isArray(data) ? data : []);
      } catch {
        if (!abort) setFans([]);
      }
    })();
    return () => { abort = true; };
  }, []);

  // Image preview
  useEffect(() => {
    if (!imgFile) { setImgPreview(""); return; }
    const url = URL.createObjectURL(imgFile);
    setImgPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imgFile]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }
  function onImgChange(e) {
    const file = e.target.files?.[0];
    if (file) setImgFile(file);
  }
  function validateStep1() {
    if (!form.name.trim()) return "Укажите название тренажёра";
    if (!form.AI) return "Выберите ИИ";
    if (!user?.id) return "Не найден пользователь. Войдите заново.";
    return "";
  }

  // === CKEditor 4 via CDN — общий загрузчик скрипта ===
  function ensureCKEScript() {
    return new Promise((resolve, reject) => {
      if (window.CKEDITOR) return resolve();
      const script = document.createElement("script");
      script.src = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js";
      script.onload = () => resolve();
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  const commonCkeConfig = {
    language: "ru",
    height: 260,
    contentsLangDirection: "ltr",
    contentsCss: [
      "https://cdn.ckeditor.com/4.22.1/full-all/contents.css",
      "body { direction: ltr; text-align: left; }",
    ],
    toolbar: [
      { name: "document", items: ["Source","-","NewPage","Preview","Print"] },
      { name: "clipboard", items: ["Cut","Copy","Paste","PasteText","PasteFromWord","-","Undo","Redo"] },
      { name: "editing",  items: ["Find","Replace","SelectAll","Scayt"] },
      { name: "forms",    items: ["Checkbox","Radio","TextField","Textarea","Select","Button","ImageButton","HiddenField"] },
      "/",
      { name: "basicstyles", items: ["Bold","Italic","Underline","Strike","Subscript","Superscript","-","RemoveFormat"] },
      { name: "paragraph",   items: ["NumberedList","BulletedList","-","Outdent","Indent","-","Blockquote","CreateDiv","-","JustifyLeft","JustifyCenter","JustifyRight","JustifyBlock","-","BidiLtr","BidiRtl","Language"] },
      { name: "links",       items: ["Link","Unlink","Anchor"] },
      { name: "insert",      items: ["Image","Table","HorizontalRule","Smiley","SpecialChar","PageBreak","Iframe"] },
      "/",
      { name: "styles", items: ["Styles","Format","Font","FontSize"] },
      { name: "colors", items: ["TextColor","BGColor"] },
      { name: "tools",  items: ["Maximize","ShowBlocks"] },
      { name: "about",  items: ["About"] },
    ],
    extraPlugins: "colorbutton,font,justify,language,bidi,div,find,smiley,iframe",
    removePlugins: "elementspath,notification",
    resize_enabled: true,
  };

  // Инициализация CKEditor для reja (план)
  useEffect(() => {
    let destroyed = false;
    ensureCKEScript().then(() => {
      if (destroyed) return;
      const editorId = "reja-editor";
      if (window.CKEDITOR.instances[editorId]) {
        window.CKEDITOR.instances[editorId].destroy(true);
      }
      const inst = window.CKEDITOR.replace(editorId, commonCkeConfig);
      inst.setData(rejaHTML || "");
      inst.on("change", () => setRejaHTML(inst.getData()));
      inst.on("instanceReady", () => { try { inst.resize('100%', 260); } catch {} });
    });
    return () => {
      destroyed = true;
      const editorId = "reja-editor";
      if (window.CKEDITOR && window.CKEDITOR.instances[editorId]) {
        window.CKEDITOR.instances[editorId].destroy(true);
      }
    };
  }, []);

  // === submit шаг 1 ===
  async function handleCreate(e) {
    e.preventDefault();
    setError(""); setSuccess("");

    const v = validateStep1();
    if (v) { setError(v); return; }

    try {
      setLoading(true);
      const token = getAnyTokenFromStorage();

      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("savol", "37");
      if (imgFile) {
        fd.append("img", imgFile);
      }
      fd.append("tushuncha_b2", "37");
      fd.append("promt", "37");
      fd.append("qisqa_tavsif", form.qisqa_tavsif.trim());
      fd.append("user", String(user.id));
      fd.append("AI", String(form.AI));
      fd.append("tartib", form.tartib.trim());
      fd.append("fan", String(form.fan));
      fd.append("reja", rejaHTML.trim());

      const res = await fetch(VAZIFALAR_ENDPOINT, {
        method: "POST",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        body: fd,
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const serverMsg = (typeof data === "string" && data) || data?.detail || data?.error || data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) || "Не удалось создать тренажёр";
        throw new Error(Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg));
      }

      const newId = data?.id ?? data?.pk;
      setSuccess("Тренажёр создан успешно!");
      setTimeout(() => navigate("/TrainerMy", { replace: true }), 1000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.message || "Ошибка отправки формы");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card}>
        <h1 className={s.title}>{t('addTren')}</h1>

        {/* Форма создания тренажёра */}
        <form className={s.form} onSubmit={handleCreate}>
          <div className={s.field}>
            <label className={s.label}>{t('addTrenName')}</label>
            <input className={s.input} name="name" value={form.name} onChange={onChange} placeholder={t('addTrenNamePl')} required />
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenImg')}</label>
            <p className={s.tavsiya}>{t('addTrenTavsiya1')} <a href="https://www.flaticon.com/search?word=ai&type=animated-icon">flaticon.com</a> {t('addTrenTavsiya2')}</p>
            <input type="file" accept="image/*" onChange={onImgChange} />
            {imgPreview && (
              <div className={s.previewBox}>
                <img src={imgPreview} alt="preview" className={s.previewImg} />
              </div>
            )}
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenDesc')}</label>
            <textarea className={s.textarea} name="qisqa_tavsif" value={form.qisqa_tavsif} onChange={onChange} placeholder={t('addTrenDescPl')} rows={3} />
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenAI')}</label>
            <select className={s.select} name="AI" value={form.AI} onChange={onChange} required>
              <option value="" disabled>{t('addTrenAIPl')}</option>
              {ais.map((ai) => (
                <option key={ai.id} value={ai.id}>{ai.name}</option>
              ))}
            </select>
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenTartib')}</label>
            <input className={s.input} name="tartib" value={form.tartib} onChange={onChange} placeholder={t('addTrenTartibPl')} />
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenFan')}</label>
            <select className={s.select} name="fan" value={form.fan} onChange={onChange} required>
              <option value="" disabled>{t('addTrenFanPl')}</option>
              {fans.map((fan) => (
                <option key={fan.id} value={fan.id}>{fan.name}</option>
              ))}
            </select>
          </div>

          <div className={s.field}>
            <label className={s.label}>{t('addTrenReja')}</label>
            {/* На этот textarea навесится CKEditor - reja */}
            <textarea id="reja-editor" defaultValue={rejaHTML} />
          </div>

          {error && <div className={s.error}>{error}</div>}
          {success && <div className={s.success}>{success}</div>}

          <div className={s.actions}>
            <button className={s.submit} type="submit" disabled={loading}>
              {loading ? "Saqlayapmiz…" : t('Yaratish')}
            </button>
            <button type="button" className={s.secondary} onClick={() => navigate(-1)} disabled={loading}>{t('bekorQilish')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
